import { useAuth0 } from '@auth0/auth0-react';
import { useEffect, useRef, useState } from 'react';
import { tokenService } from './token-service';
import { useAccountLinkedStore } from '@/stores/accountLinked';
import { useUserStore, useBusinessStore, useAuthStore } from '@/stores';
import i18n from '@/i18n/config';
import { getVisitorSessionIds } from '@/landing/visitorId';
import { readOrganicAttribution } from '@/landing/organicAttribution';
import { trackBrowserLifecycleEvents } from '@/landing/analytics';
import { identifyUser, resetTelemetry } from '@/lib/analytics/telemetry';
import type { BrowserAnalyticsEvent } from '@tilly/shared';

// Derives the same base used by client.ts so auth sync always hits the correct environment.
const AUTH_SYNC_URL = import.meta.env.VITE_API_URL
  ? `${import.meta.env.VITE_API_URL}/api/auth/sync`
  : '/api/auth/sync';

interface SyncResponse {
  user: {
    id: string;
    email: string;
    name: string;
    locale?: string;
  };
  memberships: Array<{
    id: string;
    businessId: string;
    businessName?: string;
    role: 'owner' | 'admin' | 'provider' | 'customer';
    providerType?: 'employee' | 'contractor';
    status: 'pending' | 'approved' | 'rejected' | 'active' | 'suspended';
  }>;
  /** Present when this login was just linked to an existing account by email. */
  accountLinked?: { provider: string };
}

/**
 * Hook that syncs Auth0 user with internal user storage on login.
 * Fetches user profile and business memberships.
 *
 * Returns `synced: true` once the first successful sync has completed.
 * Use this to gate rendering of routes that depend on membership data
 * (prevents stale persisted memberships from causing wrong redirects).
 */
export function useAuthSync() {
  const { isAuthenticated, user, getIdTokenClaims } = useAuth0();
  const setUser = useUserStore((state) => state.setUser);
  const setMemberships = useBusinessStore((state) => state.setMemberships);
  const login = useAuthStore((state) => state.login);
  const logout = useAuthStore((state) => state.logout);
  const syncedRef = useRef(false);
  const syncInProgressRef = useRef(false);
  const [synced, setSynced] = useState(false);

  useEffect(() => {
    // Bridge mode: BridgePage already set user/membership stores via direct sync.
    // The Auth0 SDK won't be authenticated, so we mark synced here to ungate the shell.
    if (!isAuthenticated && tokenService.hasOverrideToken() && !syncedRef.current) {
      syncedRef.current = true;
      setSynced(true);
      return;
    }

    async function syncUser() {
      // Only sync once when authenticated
      if (!isAuthenticated || !user || syncedRef.current || syncInProgressRef.current) {
        return;
      }

      syncInProgressRef.current = true;

      try {
        const token = await tokenService.getToken();
        if (!token) {
          console.error('[useAuthSync] No token available');
          syncInProgressRef.current = false;
          return;
        }

        // Signed ID token lets the worker trust our email when linking accounts.
        const idToken = (await getIdTokenClaims())?.__raw;

        // Sync user with internal API
        const response = await fetch(AUTH_SYNC_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            locale: i18n.resolvedLanguage === 'es-MX' ? 'es-MX' : 'en-US',
            idToken,
            analytics:
              localStorage.getItem('tilly_consent') === 'granted'
                ? { ...getVisitorSessionIds(), attribution: readOrganicAttribution() }
                : undefined,
          }),
        });

        if (!response.ok) {
          console.error('[useAuthSync] Failed to sync user:', await response.text());
          syncInProgressRef.current = false;
          return;
        }

        const payload = (await response.json()) as
          { data?: SyncResponse; analytics_events?: BrowserAnalyticsEvent[] } | SyncResponse;
        // Server wraps successful responses as `{ data: {...} }`; unwrap if present.
        const data: SyncResponse =
          'data' in payload && payload.data
            ? (payload.data as SyncResponse)
            : (payload as SyncResponse);
        if ('analytics_events' in payload && payload.analytics_events?.length) {
          trackBrowserLifecycleEvents(payload.analytics_events);
        }
        console.log('[useAuthSync] User synced:', data);
        if (data.accountLinked) useAccountLinkedStore.getState().show(data.accountLinked.provider);

        // Store user profile
        if (data.user) {
          setUser({
            id: data.user.id,
            email: data.user.email,
            name: data.user.name,
          });

          // Log in to auth store with internal user ID (token is owned by tokenService)
          login(data.user.id);
          // Internal id only — never email/name.
          identifyUser(data.user.id);
          if (data.user.locale) {
            await i18n.changeLanguage(data.user.locale === 'es-MX' ? 'es-MX' : 'en');
          }
        }

        // Always sync memberships to match server state — even when empty.
        // Not calling setMemberships when memberships are empty would leave
        // stale business IDs from a previous session in the persisted store.
        setMemberships(data.memberships ?? []);

        syncedRef.current = true;
        setSynced(true);
      } catch (err) {
        console.error('[useAuthSync] Auth sync error:', err);
        // Still mark as synced so the app doesn't hang on the spinner.
        // The user is authenticated (has id_token) — let them through so
        // RequireAuth / the API layer can handle token issues properly.
        syncedRef.current = true;
        setSynced(true);
      } finally {
        syncInProgressRef.current = false;
      }
    }

    syncUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally using user?.sub to avoid re-syncing on every user object reference change
  }, [isAuthenticated, user?.sub, setUser, setMemberships, login]);

  // Handle logout — skip if a mobile session bridge is active.
  // In bridge mode, Auth0 isAuthenticated is always false, but the user IS
  // authenticated via the override token set by BridgePage.
  useEffect(() => {
    if (!isAuthenticated && !tokenService.hasOverrideToken()) {
      logout();
      useUserStore.getState().clearUser();
      useBusinessStore.getState().clearMemberships();
      resetTelemetry();
      syncedRef.current = false;
      setSynced(false);
    }
  }, [isAuthenticated, logout]);

  return { synced };
}
